
import { useState, useCallback } from 'react';
import { Email, EmailStatistics, ComposeDraft, UserEmailConfig, UserDetails } from '../types';

export const useEmailState = () => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [currentEmail, setCurrentEmail] = useState<Email | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDbReady, setIsDbReady] = useState(true);
  const [currentFolder, setCurrentFolder] = useState('inbox');
  const [searchQuery, setSearchQuery] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDraft, setComposeDraft] = useState<ComposeDraft>({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    attachments: []
  });
  const [statistics, setStatistics] = useState<EmailStatistics>({
    unread: 0,
    total: { inbox: 0, sent: 0, drafts: 0, trash: 0 }
  });
  const [userEmailConfig, setUserEmailConfig] = useState<UserEmailConfig | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [hasMoreEmails, setHasMoreEmails] = useState(true);
  
  // Pagination state
  const [totalEmailsInFolder, setTotalEmailsInFolder] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const folders = [
    { id: 'inbox', name: 'Inbox', count: statistics.total.inbox },
    { id: 'sent', name: 'Sent', count: statistics.total.sent },
    { id: 'drafts', name: 'Drafts', count: statistics.total.drafts },
    { id: 'trash', name: 'Trash', count: statistics.total.trash }
  ];

  // Stable callback functions to prevent unnecessary re-renders
  const stableSetEmails = useCallback((newEmails: Email[]) => {
    setEmails(newEmails);
  }, []);

  const stableSetCurrentEmail = useCallback((email: Email | null) => {
    setCurrentEmail(email);
  }, []);

  const stableSetLoading = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  const stableSetError = useCallback((hasError: boolean) => {
    setError(hasError ? 'Email operation failed' : null);
  }, []);

  const stableSetStatistics = useCallback((stats: EmailStatistics) => {
    setStatistics(stats);
  }, []);

  const stableSetUserEmailConfig = useCallback((config: UserEmailConfig | null) => {
    setUserEmailConfig(config);
  }, []);

  const stableSetUserDetails = useCallback((details: UserDetails | null) => {
    setUserDetails(details);
  }, []);

  const stableSetHasMoreEmails = useCallback((hasMore: boolean) => {
    setHasMoreEmails(hasMore);
  }, []);

  const stableSetTotalEmailsInFolder = useCallback((total: number) => {
    setTotalEmailsInFolder(total);
  }, []);

  const stableSetTotalPages = useCallback((pages: number) => {
    setTotalPages(pages);
  }, []);

  const stableSetCurrentPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const stableSetPageSize = useCallback((size: number) => {
    setPageSize(size);
  }, []);

  return {
    // State
    emails,
    currentEmail,
    loading,
    error,
    isDbReady,
    currentFolder,
    searchQuery,
    composeOpen,
    composeDraft,
    statistics,
    folders,
    userEmailConfig,
    userDetails,
    hasMoreEmails,
    totalEmailsInFolder,
    totalPages,
    currentPage,
    pageSize,
    
    // Setters
    setEmails,
    setCurrentEmail,
    setLoading,
    setError,
    setIsDbReady,
    setCurrentFolder,
    setSearchQuery,
    setComposeOpen,
    setComposeDraft,
    setStatistics,
    
    // Stable callbacks
    stableSetEmails,
    stableSetCurrentEmail,
    stableSetLoading,
    stableSetError,
    stableSetStatistics,
    stableSetUserEmailConfig,
    stableSetUserDetails,
    stableSetHasMoreEmails,
    stableSetTotalEmailsInFolder,
    stableSetTotalPages,
    stableSetCurrentPage,
    stableSetPageSize,
  };
};
